<template>
    <div class="custom-candidate-attributes">
        <div
            v-for="(attr, index) in customAttributes"
            :key="`custom_attribute_${attr.id}_${index}`"
            class="afu-mb-7"
        >
            <div class="afu-flex afu-items-center afu-justify-between">
                <label class="af-label afu-text-black afu-block afu-mb-2 af-text--7xl afu-font-semibold">
                    {{ attr.title }}
                </label>
            </div>
            <div v-if="attr.type === 'text'">
                <div class="afu-relative">
                    <input
                        v-model="attr.value"
                        type="text"
                        :disabled="attr.disabled"
                        class="afu-box-border af-input afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-border-inputBorder-400 focus:afu-border-inputBorder-600 afu-bg-white afu-h-10 afu-py-2 afu-pr-3 afu-pl-3"
                        @blur="updateAttribute(attr)"
                        @keyup.enter="updateAttribute(attr)"
                    >
                    <div
                        v-if="attr.title === 'Video introduction link (URL)'"
                        class="afu-absolute afu-inset-y-0 afu-right-0 afu-pr-3 afu-flex afu-items-center afu-cursor-pointer"
                    >
                        <FontAwesomeIcon
                            :icon="['fal', 'lock-alt']"
                            class="afu-text-primary-light"
                        />
                    </div>
                </div>
                <div
                    v-if="attr.title === 'Video introduction 2024'"
                    class="af-no-result-found afu-text-md afu-mt-5 afu-text-primary-light"
                >
                    This field will be updated within 3 business days of completing your <a
                        href="/video-introduction/"
                        target="_blank"
                    >video introduction</a>
                </div>
                <div
                    v-else
                    class="af-no-result-found afu-text-md afu-mt-5 afu-text-primary-light"
                >
                    {{ attr.description }}
                </div>
                <small
                    v-if="checkError(attr)"
                    class="af-help-text afu-block afu-mt-1 afu-text-danger-400"
                >
                    {{ attr.error }}
                </small>
            </div>
            <div v-if="attr.type === 'textarea'">
                <textarea
                    v-model="attr.value"
                    class="afu-box-border af-input afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-border-inputBorder-400 focus:afu-border-inputBorder-600 afu-bg-white afu-h-150 afu-py-2 afu-pr-3 afu-pl-3"
                    :disabled="attr.disabled"
                    @blur="updateAttribute(attr)"
                    @keyup.enter="updateAttribute(attr)"
                    :maxlength=getMaxLength(attr)
                />
                <div 
                    v-if="attr.rules.max > 0" 
                    class="textarea-char-count afu-float-right"
                    :class="(attr.value && attr.value.length >= attr.rules.max) ? 'afu-font-bold': ''"
                >
                    <span v-if="attr.value" class="current">{{ attr.value.length }}</span>
                    <span v-else class="current">0</span>
                    <span class="maximum">/{{ attr.rules.max }}</span>
                </div>
                <div class="af-no-result-found afu-text-md afu-mt-5 afu-text-primary-light">
                    {{ attr.description }}
                </div>
            </div>
            <div v-if="attr.type === 'number'">
                <input
                    v-model="attr.value"
                    type="number"
                    :disabled="attr.disabled"
                    class="afu-box-border af-input afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-border-inputBorder-400 focus:afu-border-inputBorder-600 afu-bg-white afu-h-10 afu-py-2 afu-pr-3 afu-pl-3"
                    @blur="updateAttribute(attr)"
                    @keyup.enter="updateAttribute(attr)"
                >
                <small
                    v-if="checkError(attr)"
                    class="af-help-text afu-block afu-mt-1 afu-text-danger-400"
                >
                    {{ attr.error }}
                </small>
            </div>
            <div v-if="attr.type === 'select'">
                <div class="af-attributes-group-container afu-flex afu-flex-wrap afu-flex-col md:afu-flex-row md:afu-items-center afu-bg-white afu-border afu-border-solid afu-border-neutral afu-px-4 afu-pt-4 afu-pb-2 afu-rounded-card">
                    <div
                        v-for="(option, index2) in attr._options"
                        :key="`${attr.id}_${index2}`"
                        class="af-attributes-group-item afu-inline-flex afu-items-center afu-rounded-btn afu-px-4 afu-py-2 sm:afu-mr-2 afu-mb-2.5 afu-cursor-pointer afu-transition afu-ease-in-out afu-duration-150 af-text--lg afu-border afu-border-solid"
                        :class="{
                            'afu-text-secondary afu-bg-white afu-border-neutral': !option.is_selected,
                            'is-active afu-text-white afu-bg-primary afu-border-primary': option.is_selected,
                        }"
                        @click="updateSelectAttribute(attr, option.value)"
                    >
                        <FontAwesomeIcon
                            :icon="option.is_selected ? ['far', 'check-square'] : ['fas', 'square']"
                            class="af-attributes-group-icon"
                            :class="{
                                'afu-text-neutral': !option.is_selected,
                            }"
                            style="font-size: 16px;"
                        />

                        <div class="af-attributes-group-label afu-ml-2 afu-text-sm">
                            {{ option.value }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="attr.type === 'multiselect'">
                <div class="af-attributes-group-container afu-flex afu-flex-wrap afu-flex-col md:afu-flex-row md:afu-items-center afu-bg-white afu-border afu-border-solid afu-border-neutral afu-px-4 afu-pt-4 afu-pb-2 afu-rounded-card">
                    <div
                        v-for="(option, index2) in attr._options"
                        :key="`${attr.id}_${index2}`"
                        class="af-attributes-group-item afu-inline-flex afu-items-center afu-rounded-btn afu-px-4 afu-py-2 sm:afu-mr-2 afu-mb-2.5 afu-cursor-pointer afu-transition afu-ease-in-out afu-duration-150 af-text--lg afu-border afu-border-solid"
                        :class="{
                            'afu-text-secondary afu-bg-white afu-border-neutral': !option.is_selected,
                            'is-active afu-text-white afu-bg-primary afu-border-primary': option.is_selected,
                        }"
                        @click="updateMultiSelectAttribute(attr, option.value)"
                    >
                        <FontAwesomeIcon
                            :icon="option.is_selected ? ['far', 'check-square'] : ['fas', 'square']"
                            class="af-attributes-group-icon"
                            :class="{
                                'afu-text-neutral': !option.is_selected,
                            }"
                            style="font-size: 16px;"
                        />

                        <div class="af-attributes-group-label afu-ml-2 afu-text-sm">
                            {{ option.value }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="attr.type === 'checkbox'">
                <div class="af-attributes-group-container afu-flex afu-flex-wrap afu-flex-col md:afu-flex-row md:afu-items-center afu-bg-white afu-border afu-border-solid afu-border-neutral afu-px-4 afu-pt-4 afu-pb-2 afu-rounded-card">
                    <span>
                        <input
                            type="checkbox"
                            v-model="attr.value"
                            @click="updateCheckboxAttribute(attr.id, attr.value)"
                        >
                    </span>
                    <span class="afu-pl-2">
                        {{ attr.description }}
                    </span>
                </div>
            </div>
            <div v-if="attr.type === 'email'">
                <div>
                    <div>
                        <input
                            type="text"
                            v-model="attr.value"
                            :disabled="attr.disabled"
                            class="afu-box-border af-input afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-border-inputBorder-400 focus:afu-border-inputBorder-600 afu-bg-white afu-h-10 afu-py-2 afu-pr-3 afu-pl-3"
                            @blur="updateAttribute(attr)"
                            @keyup.enter="updateAttribute(attr)"
                        >
                        <small
                            v-if="checkError(attr)"
                            class="af-help-text afu-block afu-mt-1 afu-text-danger-400"
                        >
                            {{ attr.error }}
                        </small>
                    </div>
                    <div class="af-no-result-found afu-text-lg afu-font-semibold afu-mt-5 afu-text-neutral-dark">
                        {{ attr.description }}
                    </div>
                </div>
            </div>
            <div v-if="attr.type === 'phone'">
                <div>
                    <input
                        type="text"
                        v-model="attr.value"
                        :disabled="attr.disabled"
                        class="afu-box-border af-input afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-border-inputBorder-400 focus:afu-border-inputBorder-600 afu-bg-white afu-h-10 afu-py-2 afu-pr-3 afu-pl-3"
                        @blur="updateAttribute(attr)"
                        @keyup.enter="updateAttribute(attr)"
                    >
                    <small
                        v-if="checkError(attr)"
                        class="af-help-text afu-block afu-mt-1 afu-text-danger-400"
                    >
                        {{ attr.error }}
                    </small>
                </div>
                <div class="af-no-result-found afu-text-lg afu-font-semibold afu-mt-5 afu-text-neutral-dark">
                    {{ attr.description }}
                </div>
            </div>
            <div v-if="attr.type === 'file'">
                <div
                    v-if="!attr.value"
                    class="afu-text-black afu-text-sm afu-mt-2 afu-bg-white"
                >
                    <C1Button
                        id="uploadFileBtn"
                        variant="primary"
                        @click.stop.prevent="openFileModal(attr.id)"
                    >
                        <FontAwesomeIcon
                            :icon="['far', 'plus']"
                            class="afu-mr-2"
                        />
                        Add a file
                    </C1Button>
                    <input
                        :ref="`file-${attr.id}`"
                        type="file"
                        @change="uploadingFile($event, attr)"
                        style="display: none;"
                    >
                </div>
                <div
                    v-else
                    class="afu-border-b afu-border-solid afu-border-t-0 afu-border-r-0 afu-border-l-0 afu-border-neutral afu-mt-2 afu-p-1 afu-flex afu-flex-col md:afu-flex-row md:afu-items-center afu-justify-between"
                >
                    <div>
                        <div class="afu-text-mds afu-font-semibold afu-text-black afu-mb-2">
                            {{ attr.file.file_name }}
                        </div>

                        <div class="afu-text-sm afu-text-neutral-dark">
                            {{ $t('resume.uploaded') }} {{ attr.file.created_at }}
                        </div>
                    </div>

                    <div class="afu-mt-3 md:afu-mt-0">
                        <C1Button
                            variant="neutral"
                            @click="downloadFile(attr.file.uuid)"
                        >
                            <FontAwesomeIcon
                                :icon="['fal', 'arrow-to-bottom']"
                                class="afu-mr-2"
                                fixed-width
                            />

                            {{ $t('label.download') }}
                        </C1Button>

                        <C1Button
                            variant="neutral"
                            class="afu-ml-2"
                            @click="removeFile(attr)"
                        >
                            <FontAwesomeIcon
                                :icon="['fal', 'trash-alt']"
                                class="afu-mr-2"
                                fixed-width
                            />

                            {{ $t('label.remove') }}
                        </C1Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex';
    import C1Button from '@/components/C1Components/C1Button';

    export default {
        name: 'SeekerCustomAttributes',

        components: {
            C1Button,
        },

        props: {
            attributeGroup: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                customAttributes: [],
                attributeOptions: [],
                selectedFile: null,
            };
        },

        computed: {
            ...mapState({
                $_meAttributeOptions: state => state.me.attributeOptions,
            }),

            seekerAttributeOptions() {
                return this.$_meAttributeOptions.filter(
                    o => o.attribute_code === this.attributeCode,
                );
            },

            isAllSelected() {
                return (
                    this.attributeOptions.filter(o => o.is_selected).length ===
                    this.attributeOptions.length
                );
            },
        },

        created() {
            this.getCustomAttributes();
        },

        methods: {
            ...mapMutations('me', {
                $_STORE_ATTRIBUTE_OPTION: 'STORE_ATTRIBUTE_OPTION',
            }),

            ...mapActions('me', {
                $_syncAttributes: 'getCustomAttributes',
                $_getAttributes: 'getAttributes',
                $_getCustomAttributes: 'getCustomAttributes',
                $_updateCustomAttributes: 'updateCustomAttributes',
                $_storeCustomFieldDocument: 'storeCustomFieldDocument',
                $_downloadCustomFieldDocument: 'downloadCustomFieldDocument',
                $_removeCustomFieldDocument: 'removeCustomFieldDocument',
            }),

            checkError(attr) {
                return attr?.error ? true : false;
            },

            getCustomAttributes() {
                const group = this.attributeGroup;
                this.$_getCustomAttributes({ group }).then(res => {
                    if (res.data) {
                        res.data.forEach(attr => {
                            attr.error = '';
                            attr.disabled = false;

                            if (attr.title === 'Video introduction 2024') {
                                attr.disabled = true;
                            }

                            if (attr.type === 'select') {
                                const ops = [];
                                attr.options.forEach(op => {
                                    if (attr.value === op) {
                                        ops.push({
                                            value: op,
                                            is_selected: true,
                                        });
                                    } else {
                                        ops.push({
                                            value: op,
                                            is_selected: false,
                                        });
                                    }
                                });
                                attr._options = ops;
                            }

                            if (attr.type === 'multiselect') {
                                const ops = [];
                                attr.options.forEach(op => {
                                    if (attr.value && attr.value.includes(op)) {
                                        ops.push({
                                            value: op,
                                            is_selected: true,
                                        });
                                    } else {
                                        ops.push({
                                            value: op,
                                            is_selected: false,
                                        });
                                    }
                                });
                                attr._options = ops;
                            }
                        });
                        this.customAttributes = res.data;
                    }
                });
            },

            updateAttribute(attr) {
                const valid = this.validateAttribute(attr);
                if (valid) {
                    attr.error = valid;
                    console.log(attr.error);
                    return;
                }

                this.$_updateCustomAttributes({
                    id: attr.id,
                    value: attr.value,
                }).then(res => {
                    console.log(res);
                });
            },

            validateAttribute(attr) {
                attr.error = '';
                if (attr.rules) {
                    if (attr.type === 'phone') {
                        const regular_expression = attr.rules.regex;
                        if (regular_expression) {
                            if (!attr.value.match(regular_expression)) {
                                return 'Invalid phone format';
                            }
                        }
                    }

                    if (attr.type === 'email') {
                        // eslint-disable-next-line no-useless-escape
                        const regular_expression = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
                        if (regular_expression) {
                            if (!attr.value.match(regular_expression)) {
                                return 'Invalid email';
                            }
                        }
                    }

                    if (attr.type === 'number') {
                        const v = parseInt(attr.value);
                        if (v > attr.rules.max || v < attr.rules.min) {
                            return `The value should be between ${attr.rules.min} and ${attr.rules.max}`;
                        }
                    }

                    if (attr.type === 'text' || attr.type === 'textarea') {
                        if (attr.rules.max > 0) {
                            if (attr.value.length > attr.rules.max) {
                                return `The maximum number of characters allowed is ${attr.rules.max}. Answers exceeding this limit will not be saved.`;
                            }
                        }
                    }
                }

                return false;
            },

            updateSelectAttribute(attr, value) {
                attr._options.forEach(a => {
                    if (a.value === value) {
                        a.is_selected = true;
                    } else {
                        a.is_selected = false;
                    }
                });
                this.$_updateCustomAttributes({ id: attr.id, value }).then(res => {
                    console.log(res);
                });
            },

            updateMultiSelectAttribute(attr, value) {
                attr._options.forEach(a => {
                    if (a.value === value) {
                        a.is_selected = !a.is_selected;
                    }
                });

                const values = [];
                attr._options.forEach(a => {
                    if (a.is_selected) {
                        values.push(a.value);
                    }
                });
                this.$_updateCustomAttributes({ id: attr.id, value: values }).then(
                    res => {
                        console.log(res);
                    },
                );
            },

            updateCheckboxAttribute(id, v) {
                let value = false;
                if (v) {
                    value = !v;
                } else {
                    value = true;
                }
                this.$_updateCustomAttributes({ id, value }).then(res => {
                    console.log(res);
                });
            },

            openFileModal(id) {
                const ref = `file-${id}`;
                this.$refs[ref][0].click();
            },

            async uploadingFile(e, attr) {
                attr.error = '';
                const file = e.target.files[0];
                if (file) {
                    const savingRes = await this.$_storeCustomFieldDocument(file);
                    if (savingRes.uuid) {
                        await this.$_updateCustomAttributes({
                            id: attr.id,
                            value: savingRes.uuid,
                        });

                        attr.file = savingRes;
                        attr.value = savingRes.uuid;
                    } else {
                        attr.error = 'Cannot upload file, please try again';
                    }
                }
            },

            async removeFile(attr) {
                await this.$_removeCustomFieldDocument(attr.file.uuid);
                await this.$_updateCustomAttributes({
                    id: attr.id,
                    value: '',
                });
                attr.file = null;
                attr.value = '';
            },

            downloadFile(fileId) {
                this.$_downloadCustomFieldDocument(fileId).then(response => {
                    if (navigator.userAgent.match('CriOS')) {
                        //Chrome iOS
                        var reader = new FileReader();
                        reader.onloadend = () => {
                            // window.open(reader.result);
                            const link = document.createElement('a');
                            link.href = reader.result;
                            link.setAttribute(
                                'download',
                                response.headers['resume-filename'],
                            );
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                        };
                        reader.readAsDataURL(new Blob([response.data]));
                    } else {
                        window.URL = window.URL || window.webkitURL;
                        const url = window.URL.createObjectURL(
                            new Blob([response.data]),
                        );
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute(
                            'download',
                            response.headers['resume-filename'],
                        );
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }
                });
            },

            getMaxLength(attr) {
                return attr.rules.max > 0 ? attr.rules.max : '';
            },
        },
    };
</script>
<style
    lang="scss"
    scoped
>
    input:disabled {
        color: #95aac9;
        background-color: #edf2f9;
    }
</style>